// src/features/admin/components/NotificationRow.tsx
import React from "react";
import { NotificationOut } from "api/admin";

const GOLD = "#f3dfae";
const NAVY = "#151A3A";

interface Props {
  notification: NotificationOut;
  onRetry: (n: NotificationOut) => void;
  onAcknowledge: (n: NotificationOut) => void;
  onView: (n: NotificationOut) => void;
}

// The backend's STALE_SENDING_AFTER (notification_service.py). A row 'sending' for
// longer was left by a process that died mid-send, and retry sends it again. The
// server decides; this only says when the button is worth showing.
const STALE_SENDING_MS = 15 * 60 * 1000;

export const isStuckSending = (n: NotificationOut, now: number = Date.now()): boolean => {
  if (n.status !== "sending" || !n.last_updated_date) return false;
  const claimed = new Date(n.last_updated_date).getTime();
  return !Number.isNaN(claimed) && now - claimed >= STALE_SENDING_MS;
};

const badgeClass = (status: string) => {
  switch (status) {
    case "queued":
      return "bg-[#f3dfae]/60 text-[#151A3A] border-[#f3dfae]/80";
    case "failed":
      return "bg-rose-50 text-rose-800 border-rose-200";
    case "sent":
      return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "acknowledged":
      return "bg-slate-50 text-slate-800 border-slate-200";
    default:
      return "bg-slate-50 text-slate-800 border-slate-200";
  }
};

const NotificationRow: React.FC<Props> = ({ notification: n, onRetry, onAcknowledge, onView }) => {
  const created = n.created_date ? new Date(n.created_date).toLocaleString() : "—";
  const lastError = (n.last_error || "").trim();
  // A row can cross the limit while the page is open. Nothing else re-renders it
  // then, so wake once at the limit to show the warning and the Retry button.
  const [, wake] = React.useReducer((x: number) => x + 1, 0);
  const stuck = isStuckSending(n);
  React.useEffect(() => {
    if (n.status !== "sending" || !n.last_updated_date || stuck) return;
    const claimed = new Date(n.last_updated_date).getTime();
    if (Number.isNaN(claimed)) return;
    const t = setTimeout(wake, Math.max(0, claimed + STALE_SENDING_MS - Date.now()));
    return () => clearTimeout(t);
  }, [n.status, n.last_updated_date, stuck]);

  return (
    <div className="border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={[
              badgeClass(n.status),
              "inline-flex items-center rounded-full border px-3 py-1",
              "font-ui text-xs uppercase tracking-wide"
            ].join(" ")}
          >
            {n.status}
          </span>

          <span className="font-ui text-xs uppercase tracking-wide text-slate-500">#{n.id}</span>
          <span className="font-ui text-xs uppercase tracking-wide text-slate-500">v{n.lock_version}</span>
          <span className="font-ui text-xs uppercase tracking-wide text-slate-600">{n.kind}</span>
        </div>

        <div className="font-body text-sm text-slate-800 mt-2 break-words">
          <span className="text-slate-500">Order:</span> {n.order_id ?? "—"}{" "}
          <span className="text-slate-400">·</span>{" "}
          <span className="text-slate-500">Clinic:</span> {n.clinic_name ?? "—"}{" "}
          <span className="text-slate-400">·</span>{" "}
          <span className="text-slate-500">User:</span> {n.user_email ?? "—"}
        </div>

        {lastError && (
          <div className="font-body text-sm text-rose-800 mt-2 truncate">
            <span className="text-rose-700">Last error:</span> {lastError}
          </div>
        )}

        {stuck && (
          <div className="font-body text-sm text-rose-800 mt-2">
            Stuck sending since {new Date(n.last_updated_date as string).toLocaleString()}. Retry may send a
            second copy if the first one went.
          </div>
        )}

        <div className="font-ui text-xs uppercase tracking-wide text-slate-500 mt-2">
          Attempts {n.attempt_count} · Created {created}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onView(n)}
          className={[
            "inline-flex items-center justify-center rounded-full",
            "border border-slate-200 bg-white",
            "px-4 py-2",
            "text-xs uppercase tracking-wide text-[#151A3A]",
            "hover:bg-slate-50 transition-colors font-ui font-normal"
          ].join(" ")}
        >
          View
        </button>

        {(n.status === "failed" || n.status === "queued" || stuck) && (
          <button
            type="button"
            onClick={() => onRetry(n)}
            className={[
              "inline-flex items-center justify-center rounded-full",
              "px-4 py-2",
              "text-xs uppercase tracking-wide text-[#151A3A]",
              "hover:opacity-95 transition-opacity font-ui font-normal"
            ].join(" ")}
            style={{ backgroundColor: GOLD }}
          >
            Retry
          </button>
        )}

        {n.status !== "acknowledged" && (
          <button
            type="button"
            onClick={() => onAcknowledge(n)}
            className={[
              "inline-flex items-center justify-center rounded-full",
              "px-4 py-2",
              "text-xs uppercase tracking-wide text-white",
              "hover:opacity-95 transition-opacity font-ui font-normal"
            ].join(" ")}
            style={{ backgroundColor: NAVY }}
          >
            Acknowledge
          </button>
        )}
      </div>
    </div>
  );
};

export default NotificationRow;
